import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { Input } from '@/components/ui/Input'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { ReservationCourtSummaryPanel } from '@/components/venue/ReservationCourtSummaryPanel'
import { ReservationListTable } from '@/components/venue/ReservationListTable'
import { useCourts } from '@/hooks/useCourts'
import { useReservationAvailability } from '@/hooks/useReservationAvailability'
import { useVenueReservationBoard } from '@/hooks/useVenueReservationBoard'
import { useVenueReservations } from '@/hooks/useVenueReservations'
import { cn } from '@/lib/utils/cn'
import { todayDateKey } from '@/lib/venue/calendarGrid'
import type { VenueReservationStatus } from '@/lib/venue/reservationTypes'
import {
  RESERVATION_STATUS_OPTIONS,
  defaultRecentPeriod,
  isSingleDayFilter,
  validateReservationPeriod,
  type ReservationListFilter,
} from '@/lib/venue/reservationViewModel'
import { useVenueContext } from '@/routes/venueContext'

function initialFilter(): ReservationListFilter {
  return { ...defaultRecentPeriod(todayDateKey()), courtId: 'ALL', statuses: [] }
}

/**
 * 코트별 예약 현황 목록(설계 §2-4(2)) — 날짜·코트·상태 필터, 전체 건수, 목록 + 상세 진입,
 * 선택 시간대 요약(단일 날짜 필터 시). 신규 백엔드 EP 없음 — #666 (c) 목록 EP 를 소비한다.
 */
export function VenueBookingsListPage() {
  const { venueId } = useVenueContext()
  const navigate = useNavigate()

  const [appliedFilter, setAppliedFilter] = useState<ReservationListFilter>(initialFilter)
  const [dateInputs, setDateInputs] = useState({ from: appliedFilter.from, to: appliedFilter.to })

  const courtsQuery = useCourts(venueId)
  const availability = useReservationAvailability(venueId)
  const listQuery = useVenueReservations(venueId, appliedFilter)

  const singleDay = isSingleDayFilter(appliedFilter)
  const boardQuery = useVenueReservationBoard(venueId, {
    date: appliedFilter.from,
    courtId: appliedFilter.courtId,
  })

  const periodError = validateReservationPeriod(dateInputs)
  const courts = courtsQuery.data?.courts.map((court) => ({ courtId: court.courtId, name: court.name })) ?? []
  const pages = listQuery.data?.pages ?? []
  const items = pages.flatMap((page) => page.reservations)
  const lastPage = pages.at(-1)

  function applyDateRange() {
    if (periodError) return
    setAppliedFilter((current) => ({ ...current, from: dateInputs.from, to: dateInputs.to }))
  }

  function toggleStatus(status: VenueReservationStatus) {
    setAppliedFilter((current) => ({
      ...current,
      statuses: current.statuses.includes(status)
        ? current.statuses.filter((value) => value !== status)
        : [...current.statuses, status],
    }))
  }

  return (
    <>
      <PageHeader title="예약 목록" description="날짜·코트·상태로 예약을 조회합니다." />

      {!availability.isLoading && !availability.reservationAvailable ? (
        <Alert tone="warning" className="mb-4">
          <p>예약 기능이 꺼져 있어 예약 등록·변경 기능을 사용할 수 없습니다. 조회는 계속 가능합니다.</p>
          <Link
            to={`/venues/${venueId}/reservation-setting`}
            className="mt-1 inline-block text-brand-500 underline"
          >
            예약 기능 설정으로 이동
          </Link>
        </Alert>
      ) : null}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs text-slateish-600" htmlFor="reservation-list-from">
            시작일
          </label>
          <Input
            id="reservation-list-from"
            type="date"
            value={dateInputs.from}
            onChange={(event) => {
              setDateInputs((current) => ({ ...current, from: event.target.value }))
            }}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slateish-600" htmlFor="reservation-list-to">
            종료일
          </label>
          <Input
            id="reservation-list-to"
            type="date"
            value={dateInputs.to}
            onChange={(event) => {
              setDateInputs((current) => ({ ...current, to: event.target.value }))
            }}
          />
        </div>
        <div className="w-40">
          <label className="mb-1 block text-xs text-slateish-600" htmlFor="reservation-list-court">
            코트
          </label>
          <Select
            id="reservation-list-court"
            value={appliedFilter.courtId}
            onChange={(event) => {
              const value = event.target.value
              setAppliedFilter((current) => ({
                ...current,
                courtId: value === 'ALL' ? 'ALL' : Number(value),
              }))
            }}
          >
            <option value="ALL">전체 코트</option>
            {courts.map((court) => (
              <option key={court.courtId} value={court.courtId}>
                {court.name}
              </option>
            ))}
          </Select>
        </div>
        <Button size="md" disabled={Boolean(periodError)} onClick={applyDateRange}>
          조회
        </Button>
      </div>

      {periodError ? <p className="mb-3 text-xs text-danger-500">{periodError}</p> : null}

      <div role="group" aria-label="예약 상태 필터" className="mb-4 flex flex-wrap gap-2">
        {RESERVATION_STATUS_OPTIONS.map((option) => {
          const selected = appliedFilter.statuses.includes(option.value)
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected}
              onClick={() => {
                toggleStatus(option.value)
              }}
              className={cn(
                'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                selected
                  ? 'border-brand-500 bg-brand-50 text-brand-500'
                  : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
              )}
            >
              {option.label}
            </button>
          )
        })}
      </div>

      {listQuery.isLoading ? <LoadingState rows={4} label="예약을 불러오는 중" /> : null}
      {listQuery.isError ? (
        <ErrorState
          error={listQuery.error}
          onRetry={() => {
            void listQuery.refetch()
          }}
        />
      ) : null}

      {lastPage ? (
        <>
          <p className="mb-3 text-sm text-slateish-600">전체 {lastPage.totalCount.toLocaleString('ko-KR')}건</p>

          {singleDay && boardQuery.data ? (
            <div className="mb-4">
              <ReservationCourtSummaryPanel courts={boardQuery.data.courts} />
            </div>
          ) : !singleDay ? (
            <div className="mb-4 grid grid-cols-3 gap-2 sm:grid-cols-6">
              {lastPage.statusCounts.map((entry) => (
                <Card key={entry.status}>
                  <CardBody className="px-3 py-2 text-center">
                    <p className="text-[11px] text-slateish-600">{entry.label}</p>
                    <p className="text-base font-semibold text-ink-700">{entry.count}</p>
                  </CardBody>
                </Card>
              ))}
            </div>
          ) : null}

          <ReservationListTable
            items={items}
            onSelect={(reservationId) => {
              navigate(`/venues/${venueId}/bookings/${reservationId}`)
            }}
            hasNextPage={listQuery.hasNextPage ?? false}
            isFetchingNextPage={listQuery.isFetchingNextPage}
            onLoadMore={() => {
              void listQuery.fetchNextPage()
            }}
          />
        </>
      ) : null}
    </>
  )
}
